import { useState } from 'react';
import {
    Modal,
    Text,
    Button,
    Group,
    Stack,
    Checkbox,
    Alert,
    Divider,
    ThemeIcon,
    List,
    LoadingOverlay
} from '@mantine/core';
import { env } from '../utils/env';
import axios from 'axios';
import { showSuccessNotification, showErrorNotification } from '../utils/notificationUtils';
import LockIcon from '../assets/icons/lock.svg';
import AlertCircleIcon from '../assets/icons/alert-circle.svg';
import CheckIcon from '../assets/icons/check.svg';

interface ConsentDialogProps {
    itemId: string;
    itemName: string;
    productsRequiringConsent: string[];
    opened: boolean;
    onClose: () => void;
    onSuccess: () => void;
    accessToken: string;
}

const productDescriptions: Record<string, string> = {
    transactions: "View your transactions and account balances",
    auth: "View account numbers and routing information",
    identity: "Verify your identity information",
    investments: "Access your investment accounts and holdings",
    assets: "Verify your assets and account balances",
    liabilities: "View your loan and credit card balances",
    income: "Verify your income and employment",
    payment_initiation: "Initiate payments",
    transfer: "Move money between your accounts",
};

export function ConsentDialog({
    itemId,
    itemName,
    productsRequiringConsent,
    opened,
    onClose,
    onSuccess,
    accessToken
}: ConsentDialogProps) {
    const [loading, setLoading] = useState(false);
    const [selectedProducts, setSelectedProducts] = useState<string[]>([...productsRequiringConsent]);
    const [error, setError] = useState<string | null>(null);

    const handleToggleProduct = (product: string) => {
        setSelectedProducts(current =>
            current.includes(product)
                ? current.filter(p => p !== product)
                : [...current, product]
        );
    };

    const handleSubmit = async (denyAll: boolean = false) => {
        try {
            setLoading(true);
            setError(null);

            const payload = {
                consented_products: denyAll ? [] : selectedProducts,
                deny_others: true,
            };

            const response = await axios.post(
                `${env.API_URL}/api/financial/items/${itemId}/consent`,
                payload,
                {
                    headers: {
                        'Authorization': `Bearer ${accessToken}`,
                        'Content-Type': 'application/json'
                    }
                }
            );

            if (response.status === 200) {
                showSuccessNotification(
                    'Consent updated',
                    denyAll
                        ? 'You have denied access to all requested products'
                        : 'Your consent preferences have been updated'
                );
                onSuccess();
                onClose();
            }
        } catch (err: any) {
            console.error('Error processing consent:', err);
            setError(err.response?.data?.detail || 'Failed to process consent. Please try again.');
            showErrorNotification(
                err,
                'Failed to update consent preferences'
            );
        } finally {
            setLoading(false);
        }
    };

    return (
        <Modal
            opened={opened}
            onClose={onClose}
            title={<Text fw={700} size="lg">Additional Permissions Required</Text>}
            size="md"
            centered
        >
            <LoadingOverlay visible={loading} />
            <Stack gap="md">
                <Alert
                    icon={<img src={AlertCircleIcon} alt="Alert" width={16} height={16} />}
                    title="Action Required"
                    color="blue"
                    radius="md"
                >
                    {itemName} is requesting additional permissions for your account.
                </Alert>

                <Text size="sm">
                    Please review the permissions below and select which ones you would like to grant:
                </Text>

                <Divider />

                <Stack gap="xs">
                    {productsRequiringConsent.map(product => (
                        <Checkbox
                            key={product}
                            label={
                                <Text fw={500}>
                                    {product.charAt(0).toUpperCase() + product.slice(1).replace('_', ' ')}
                                    <Text span c="dimmed" size="xs" ml={5}>
                                        {productDescriptions[product] || `Access to ${product}`}
                                    </Text>
                                </Text>
                            }
                            checked={selectedProducts.includes(product)}
                            onChange={() => handleToggleProduct(product)}
                        />
                    ))}
                </Stack>

                <Alert
                    icon={<img src={LockIcon} alt="Lock" width={16} height={16} />}
                    color="gray"
                    radius="md"
                    variant="light"
                >
                    <Text size="sm" fw={500}>How we use this information:</Text>
                    <List size="xs" spacing="xs" mt={5}>
                        <List.Item icon={
                            <ThemeIcon color="blue" size={16} radius="xl">
                                <img src={CheckIcon} alt="Check" width={10} height={10} />
                            </ThemeIcon>
                        }>We only access data that you explicitly consent to</List.Item>
                        <List.Item icon={
                            <ThemeIcon color="blue" size={16} radius="xl">
                                <img src={CheckIcon} alt="Check" width={10} height={10} />
                            </ThemeIcon>
                        }>Your data is encrypted and stored securely</List.Item>
                        <List.Item icon={
                            <ThemeIcon color="blue" size={16} radius="xl">
                                <img src={CheckIcon} alt="Check" width={10} height={10} />
                            </ThemeIcon>
                        }>You can revoke access at any time</List.Item>
                    </List>
                </Alert>

                {error && (
                    <Alert color="red" title="Error" withCloseButton onClose={() => setError(null)}>
                        {error}
                    </Alert>
                )}

                <Group justify="space-between" mt="lg">
                    <Button variant="outline" color="red" onClick={() => handleSubmit(true)}>
                        Deny All
                    </Button>
                    <Group>
                        <Button variant="outline" onClick={onClose}>
                            Cancel
                        </Button>
                        <Button onClick={() => handleSubmit(false)}>
                            Allow Selected
                        </Button>
                    </Group>
                </Group>
            </Stack>
        </Modal>
    );
}

export default ConsentDialog; 